// src/ryan/admin/components/tabs/RyanAdminCoupons.tsx
//
// Cupons de desconto — criação, edição, ativar/desativar e exclusão, tudo
// dentro do modal "Gerenciar cupom" por linha. Editar discountType/
// discountValue ou excluir um cupom com salesCount > 0 nunca é bloqueado —
// RyanSale.couponCode/discountAppliedCents são snapshots próprios (nunca
// dependem de populate de couponId, ver server/models/RyanSale.js e
// server/utils/ryanCoupons.js), então nada quebra tecnicamente. O único
// efeito real é a linha "receita gerada" desse cupom sumir da lista se ele
// for excluído — por isso o app avisa (com o número de vendas afetadas)
// antes de mudar tipo/valor ou excluir um cupom já usado, mas a decisão
// final de continuar é sempre do admin, nunca bloqueada aqui.

import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Check, X, Settings, Trash2 } from 'lucide-react';
import './RyanAdminCoupons.css';

const tok = () => localStorage.getItem('accessToken') || '';
const api = (path: string, opts?: RequestInit) =>
    fetch(`/api/ryan${path}`, { ...opts, headers: { Authorization: `Bearer ${tok()}`, 'Content-Type': 'application/json', ...(opts?.headers || {}) } });

interface Product { _id: string; title: string; }

interface Coupon {
    _id: string;
    code: string;
    discountType: 'percent' | 'fixed';
    discountValue: number;
    scope: 'global' | 'specific';
    productIds: string[];
    usageLimit: number | null;
    usageCount: number;
    validFrom: string | null;
    validUntil: string | null;
    active: boolean;
    status: 'active' | 'inactive' | 'expired' | 'exhausted';
    totalRevenueCents: number;
    salesCount: number;
}

const STATUS_LABELS: Record<string, string> = {
    active: 'Ativo', inactive: 'Inativo', expired: 'Expirado', exhausted: 'Esgotado',
};

function formatPrice(cents: number): string {
    return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

function fmtDate(d: string | null): string {
    return d ? new Date(d).toLocaleDateString('pt-BR') : '—';
}

function formatDiscount(c: Coupon): string {
    return c.discountType === 'percent' ? `${c.discountValue}%` : formatPrice(c.discountValue);
}

// Sanitiza a digitação do campo "Valor fixo": só dígitos + no máximo UMA
// vírgula decimal, com no máximo 2 casas depois dela. Nunca reformata pra
// "R$ X,00" aqui — isso fica só pro onBlur (ver abaixo). Reformatar a cada
// tecla (versão anterior, com bug) reinjeta os dígitos do próprio ",00" já
// mostrado de volta como se o usuário tivesse digitado eles, multiplicando
// o valor a cada keystroke.
function sanitizeFixedInput(raw: string): string {
    const stripped = raw.replace(/[^\d,]/g, '');
    const commaIdx = stripped.indexOf(',');
    if (commaIdx === -1) return stripped;
    const intPart = stripped.slice(0, commaIdx);
    const decPart = stripped.slice(commaIdx + 1).replace(/,/g, '').slice(0, 2);
    return `${intPart},${decPart}`;
}

// "5" -> 500 | "50" -> 5000 | "5,50" -> 550 (sempre centavos, formato do schema)
function parseFixedValueToCents(raw: string): number {
    const reais = parseFloat(raw.replace(',', '.'));
    return Number.isFinite(reais) ? Math.round(reais * 100) : NaN;
}

// Reaproveitado pelo form de criação e pela seção "Editar" do modal —
// mesma máscara ao vivo, evita duplicar a lógica de sanitize/blur nos dois
// lugares (ver comentário de sanitizeFixedInput acima).
const DiscountValueInput: React.FC<{
    discountType: 'percent' | 'fixed';
    value: string;
    onChange: (v: string) => void;
}> = ({ discountType, value, onChange }) => (
    <input
        className="ras-input"
        type={discountType === 'percent' ? 'number' : 'text'}
        inputMode={discountType === 'fixed' ? 'decimal' : undefined}
        min={discountType === 'percent' ? 1 : undefined}
        placeholder={discountType === 'percent' ? 'Ex: 10' : 'Ex: 5,00'}
        value={discountType === 'fixed' ? (value ? `R$ ${value}` : '') : value}
        onChange={e => onChange(discountType === 'fixed' ? sanitizeFixedInput(e.target.value) : e.target.value)}
        onBlur={() => {
            if (discountType !== 'fixed' || !value) return;
            const cents = parseFixedValueToCents(value);
            if (Number.isFinite(cents)) onChange((cents / 100).toFixed(2).replace('.', ','));
        }}
    />
);

const RyanAdminCoupons: React.FC = () => {
    const [coupons, setCoupons] = useState<Coupon[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);

    const [code, setCode] = useState('');
    const [discountType, setDiscountType] = useState<'percent' | 'fixed'>('percent');
    const [discountValue, setDiscountValue] = useState('');
    const [scope, setScope] = useState<'global' | 'specific'>('global');
    const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());
    const [usageLimit, setUsageLimit] = useState('');
    const [validFrom, setValidFrom] = useState('');
    const [validUntil, setValidUntil] = useState('');
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const load = () => {
        setLoading(true);
        Promise.all([
            api('/admin/coupons').then(r => r.ok ? r.json() : { coupons: [] }),
            api('/admin/products').then(r => r.ok ? r.json() : []),
        ]).then(([couponsRes, productsRes]) => {
            setCoupons(couponsRes.coupons || []);
            setProducts(Array.isArray(productsRes) ? productsRes : (productsRes.products || []));
            setLoading(false);
        });
    };

    useEffect(load, []);

    const toggleProduct = (id: string) => {
        setSelectedProductIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setFormError(null);
        // 'fixed' é digitado em reais no form (máscara ao vivo, ver input abaixo)
        // e convertido pra centavos aqui — RyanCoupon.discountValue sempre guarda
        // centavos nesse tipo (ver schema em server/models/RyanCoupon.js).
        const value = discountType === 'fixed' ? parseFixedValueToCents(discountValue || '0') : Number(discountValue);
        if (!code.trim()) { setFormError('Informe um código.'); return; }
        if (!Number.isFinite(value) || value <= 0) { setFormError('Informe um valor de desconto válido.'); return; }
        if (discountType === 'percent' && value > 100) { setFormError('Desconto percentual não pode passar de 100%.'); return; }

        setSaving(true);
        try {
            const res = await api('/admin/coupons', {
                method: 'POST',
                body: JSON.stringify({
                    code: code.trim(),
                    discountType,
                    discountValue: value,
                    scope,
                    productIds: scope === 'specific' ? Array.from(selectedProductIds) : [],
                    usageLimit: usageLimit ? Number(usageLimit) : null,
                    validFrom: validFrom || null,
                    validUntil: validUntil || null,
                }),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                setFormError(d.message || 'Erro ao criar cupom.');
                return;
            }
            setCode(''); setDiscountValue(''); setScope('global'); setSelectedProductIds(new Set());
            setUsageLimit(''); setValidFrom(''); setValidUntil('');
            load();
        } catch {
            setFormError('Erro de conexão.');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (id: string) => {
        const res = await api(`/admin/coupons/${id}/toggle`, { method: 'PATCH' });
        if (res.ok) {
            const d = await res.json();
            setManagingCoupon(c => (c && c._id === id) ? { ...c, active: d.coupon.active } : c);
        }
        load();
    };

    // ─── Modal "Gerenciar cupom" ──────────────────────────────────────────
    const [managingCoupon, setManagingCoupon] = useState<Coupon | null>(null);
    const [editCode, setEditCode] = useState('');
    const [editDiscountType, setEditDiscountType] = useState<'percent' | 'fixed'>('percent');
    const [editDiscountValue, setEditDiscountValue] = useState('');
    const [editScope, setEditScope] = useState<'global' | 'specific'>('global');
    const [editProductIds, setEditProductIds] = useState<Set<string>>(new Set());
    const [editUsageLimit, setEditUsageLimit] = useState('');
    const [editValidFrom, setEditValidFrom] = useState('');
    const [editValidUntil, setEditValidUntil] = useState('');
    const [editSaving, setEditSaving] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const openManage = (c: Coupon) => {
        setManagingCoupon(c);
        setEditCode(c.code);
        setEditDiscountType(c.discountType);
        setEditDiscountValue(c.discountType === 'fixed'
            ? (c.discountValue / 100).toFixed(2).replace('.', ',')
            : String(c.discountValue));
        setEditScope(c.scope);
        setEditProductIds(new Set(c.productIds));
        setEditUsageLimit(c.usageLimit !== null ? String(c.usageLimit) : '');
        setEditValidFrom(c.validFrom ? c.validFrom.slice(0, 10) : '');
        setEditValidUntil(c.validUntil ? c.validUntil.slice(0, 10) : '');
        setEditError(null);
        setDeleteConfirm(false);
    };

    const closeManage = () => { setManagingCoupon(null); setDeleteConfirm(false); };

    const toggleEditProduct = (id: string) => {
        setEditProductIds(prev => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const handleSaveEdit = async () => {
        if (!managingCoupon) return;
        setEditError(null);

        // Aviso (nunca bloqueio) quando o tipo/valor muda num cupom já usado
        // em venda concluída — RyanSale já guarda o valor aplicado na hora
        // como snapshot próprio (discountAppliedCents), então nada quebra;
        // é só pra o admin não mudar sem saber que o cupom vai passar a
        // representar um desconto diferente do que essas vendas usaram.
        const newValue = editDiscountType === 'fixed'
            ? parseFixedValueToCents(editDiscountValue || '0')
            : Number(editDiscountValue);
        const typeOrValueChanged = editDiscountType !== managingCoupon.discountType || newValue !== managingCoupon.discountValue;
        if (typeOrValueChanged && managingCoupon.salesCount > 0) {
            const ok = window.confirm(
                `Este cupom já foi usado em ${managingCoupon.salesCount} venda${managingCoupon.salesCount !== 1 ? 's' : ''} concluída${managingCoupon.salesCount !== 1 ? 's' : ''}. ` +
                'Essas vendas guardam o valor de desconto que usaram na hora (não mudam retroativamente), mas o cupom passa a aplicar/mostrar um valor diferente a partir de agora. Continuar mesmo assim?'
            );
            if (!ok) return;
        }

        if (!editCode.trim()) { setEditError('Informe um código.'); return; }
        if (!Number.isFinite(newValue) || newValue <= 0) { setEditError('Informe um valor de desconto válido.'); return; }
        if (editDiscountType === 'percent' && newValue > 100) { setEditError('Desconto percentual não pode passar de 100%.'); return; }

        setEditSaving(true);
        try {
            const res = await api(`/admin/coupons/${managingCoupon._id}`, {
                method: 'PUT',
                body: JSON.stringify({
                    code: editCode.trim(),
                    discountType: editDiscountType,
                    discountValue: newValue,
                    scope: editScope,
                    productIds: editScope === 'specific' ? Array.from(editProductIds) : [],
                    usageLimit: editUsageLimit ? Number(editUsageLimit) : null,
                    validFrom: editValidFrom || null,
                    validUntil: editValidUntil || null,
                }),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                setEditError(d.message || 'Erro ao salvar cupom.');
                return;
            }
            closeManage();
            load();
        } catch {
            setEditError('Erro de conexão.');
        } finally {
            setEditSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!managingCoupon) return;
        setDeleting(true);
        try {
            const res = await api(`/admin/coupons/${managingCoupon._id}`, { method: 'DELETE' });
            if (res.ok) { closeManage(); load(); }
        } finally {
            setDeleting(false);
        }
    };

    if (loading) {
        return <div className="ras-pane"><div className="ras-spinner-wrap"><div className="ras-spinner" /></div></div>;
    }

    return (
        <div className="ras-pane">
            <div className="ras-section">
                <h3 className="ras-section-title">Novo cupom</h3>
                <form className="rac-form" onSubmit={handleSubmit}>
                    <div className="rac-form-row">
                        <input
                            className="ras-input" placeholder="CÓDIGO" value={code}
                            onChange={e => setCode(e.target.value.toUpperCase())}
                        />
                        <select className="ras-input" value={discountType} onChange={e => setDiscountType(e.target.value as 'percent' | 'fixed')}>
                            <option value="percent">Percentual (%)</option>
                            <option value="fixed">Valor fixo (R$)</option>
                        </select>
                        <DiscountValueInput discountType={discountType} value={discountValue} onChange={setDiscountValue} />
                    </div>

                    <div className="rac-form-row">
                        <select className="ras-input" value={scope} onChange={e => setScope(e.target.value as 'global' | 'specific')}>
                            <option value="global">Vale pra todos os produtos</option>
                            <option value="specific">Restrito a produtos específicos</option>
                        </select>
                        <input
                            className="ras-input" type="number" min={1} placeholder="Limite de uso (opcional)"
                            value={usageLimit} onChange={e => setUsageLimit(e.target.value)}
                        />
                    </div>

                    {scope === 'specific' && (
                        <div className="rac-product-picker">
                            {products.map(p => (
                                <button
                                    type="button" key={p._id}
                                    className={`rac-product-chip${selectedProductIds.has(p._id) ? ' rac-product-chip--active' : ''}`}
                                    onClick={() => toggleProduct(p._id)}
                                >
                                    {selectedProductIds.has(p._id) && <Check size={12} />} {p.title}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="rac-form-row">
                        <label className="rac-date-label">
                            Válido a partir de
                            <input className="ras-input" type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)} />
                        </label>
                        <label className="rac-date-label">
                            Válido até
                            <input className="ras-input" type="date" value={validUntil} onChange={e => setValidUntil(e.target.value)} />
                        </label>
                    </div>

                    {formError && <p className="rac-form-error">{formError}</p>}
                    <button type="submit" className="ras-grant-btn" disabled={saving}>
                        {saving ? 'Criando...' : 'Criar cupom'}
                    </button>
                </form>
            </div>

            <div className="ras-section">
                <h3 className="ras-section-title">Cupons cadastrados</h3>
                {coupons.length === 0 ? (
                    <div className="rana-empty">Nenhum cupom criado ainda.</div>
                ) : (
                    <div className="rana-sessions-table-wrap">
                        <table className="rana-sessions-table">
                            <thead>
                                <tr>
                                    <th>Código</th><th>Desconto</th><th>Escopo</th>
                                    <th>Uso</th><th>Receita gerada</th><th>Status</th><th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {coupons.map(c => (
                                    <tr key={c._id}>
                                        <td><strong>{c.code}</strong></td>
                                        <td>{formatDiscount(c)}</td>
                                        <td>{c.scope === 'global' ? 'Todos' : `${c.productIds.length} produto(s)`}</td>
                                        <td>{c.usageCount}{c.usageLimit !== null ? ` / ${c.usageLimit}` : ''}</td>
                                        <td>{formatPrice(c.totalRevenueCents)} ({c.salesCount})</td>
                                        <td><span className={`rana-outcome-badge rac-status-badge--${c.status}`}>{STATUS_LABELS[c.status]}</span></td>
                                        <td>
                                            <button type="button" className="ras-manage-btn" onClick={() => openManage(c)} title="Gerenciar">
                                                <Settings size={12} /> <span className="ras-manage-btn-label">Gerenciar</span>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {managingCoupon && ReactDOM.createPortal(
                <div className="ras-modal-overlay" onClick={closeManage}>
                    <div className="ras-modal" onClick={e => e.stopPropagation()}>
                        <div className="ras-modal-header">
                            <div>
                                <div className="ras-modal-title">{managingCoupon.code}</div>
                                <span className={`rana-outcome-badge rac-status-badge--${managingCoupon.status}`}>
                                    {STATUS_LABELS[managingCoupon.status]}
                                </span>
                            </div>
                            <button className="ras-modal-close" onClick={closeManage}><X size={18} /></button>
                        </div>

                        <div className="ras-modal-body">
                            {/* Section A: Detalhes (read-only) */}
                            <div className="ras-modal-section">
                                <div className="ras-modal-section-label">Detalhes</div>
                                <div className="rac-detail-row">
                                    <span>Uso</span>
                                    <span>{managingCoupon.usageCount}{managingCoupon.usageLimit !== null ? ` / ${managingCoupon.usageLimit}` : ' (sem limite)'}</span>
                                </div>
                                <div className="rac-detail-row">
                                    <span>Vendas concluídas</span>
                                    <span>{managingCoupon.salesCount}</span>
                                </div>
                                <div className="rac-detail-row">
                                    <span>Receita gerada</span>
                                    <span>{formatPrice(managingCoupon.totalRevenueCents)}</span>
                                </div>
                                <div className="rac-detail-row">
                                    <span>Produtos vinculados</span>
                                    <span>
                                        {managingCoupon.scope === 'global'
                                            ? 'Todos'
                                            : (managingCoupon.productIds.map(id => products.find(p => p._id === id)?.title || id).join(', ') || '—')}
                                    </span>
                                </div>
                                <div className="rac-detail-row">
                                    <span>Validade</span>
                                    <span>
                                        {managingCoupon.validFrom || managingCoupon.validUntil
                                            ? `${fmtDate(managingCoupon.validFrom)} até ${fmtDate(managingCoupon.validUntil)}`
                                            : 'Sem restrição'}
                                    </span>
                                </div>
                            </div>

                            {/* Section B: Editar */}
                            <div className="ras-modal-section">
                                <div className="ras-modal-section-label">Editar</div>
                                <div className="rac-form-row">
                                    <input
                                        className="ras-input" placeholder="CÓDIGO" value={editCode}
                                        onChange={e => setEditCode(e.target.value.toUpperCase())}
                                    />
                                    <select className="ras-input" value={editDiscountType}
                                        onChange={e => setEditDiscountType(e.target.value as 'percent' | 'fixed')}>
                                        <option value="percent">Percentual (%)</option>
                                        <option value="fixed">Valor fixo (R$)</option>
                                    </select>
                                    <DiscountValueInput discountType={editDiscountType} value={editDiscountValue} onChange={setEditDiscountValue} />
                                </div>

                                <div className="rac-form-row">
                                    <select className="ras-input" value={editScope}
                                        onChange={e => setEditScope(e.target.value as 'global' | 'specific')}>
                                        <option value="global">Vale pra todos os produtos</option>
                                        <option value="specific">Restrito a produtos específicos</option>
                                    </select>
                                    <input
                                        className="ras-input" type="number" min={1} placeholder="Limite de uso (opcional)"
                                        value={editUsageLimit} onChange={e => setEditUsageLimit(e.target.value)}
                                    />
                                </div>

                                {editScope === 'specific' && (
                                    <div className="rac-product-picker">
                                        {products.map(p => (
                                            <button
                                                type="button" key={p._id}
                                                className={`rac-product-chip${editProductIds.has(p._id) ? ' rac-product-chip--active' : ''}`}
                                                onClick={() => toggleEditProduct(p._id)}
                                            >
                                                {editProductIds.has(p._id) && <Check size={12} />} {p.title}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                <div className="rac-form-row">
                                    <label className="rac-date-label">
                                        Válido a partir de
                                        <input className="ras-input" type="date" value={editValidFrom} onChange={e => setEditValidFrom(e.target.value)} />
                                    </label>
                                    <label className="rac-date-label">
                                        Válido até
                                        <input className="ras-input" type="date" value={editValidUntil} onChange={e => setEditValidUntil(e.target.value)} />
                                    </label>
                                </div>

                                {editError && <p className="rac-form-error">{editError}</p>}
                                <button type="button" className="ras-grant-btn" onClick={handleSaveEdit} disabled={editSaving}>
                                    {editSaving ? 'Salvando…' : 'Salvar alterações'}
                                </button>
                            </div>
                        </div>

                        <div className="ras-modal-footer">
                            <button type="button" className="rac-toggle-btn" onClick={() => handleToggle(managingCoupon._id)}>
                                {managingCoupon.active ? <><X size={12} /> Desativar</> : <><Check size={12} /> Ativar</>}
                            </button>
                            {deleteConfirm ? (
                                <div className="ras-modal-revoke-row">
                                    <span className="ras-modal-revoke-text">
                                        {managingCoupon.salesCount > 0
                                            ? `Excluir some com ${formatPrice(managingCoupon.totalRevenueCents)} em receita registrada (${managingCoupon.salesCount} venda${managingCoupon.salesCount !== 1 ? 's' : ''}) desta lista — as vendas em si não são afetadas. Confirmar?`
                                            : 'Excluir este cupom?'}
                                    </span>
                                    <button className="ras-btn-danger-sm" onClick={handleDelete} disabled={deleting}>
                                        {deleting ? 'Excluindo…' : 'Confirmar'}
                                    </button>
                                    <button className="ras-icon-btn" onClick={() => setDeleteConfirm(false)}><X size={14} /></button>
                                </div>
                            ) : (
                                <button className="ras-btn-danger-ghost" onClick={() => setDeleteConfirm(true)}>
                                    <Trash2 size={13} /> Excluir cupom
                                </button>
                            )}
                            <button className="ras-btn-modal-close" onClick={closeManage}>Fechar</button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default RyanAdminCoupons;
